import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MasterDataService } from 'src/app/services/master-data.service';
import { SpinnerService } from 'src/app/services/shared/spinner.service';
import { forkJoin } from 'rxjs';
import { EndCustomerService } from 'src/app/services/end-customer.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import Swal from 'sweetalert2';
import { OemManagementService } from 'src/app/services/super-admin/oem-management.service';
import { DistributorService } from 'src/app/services/distributor/distributor.service';
import { EmailService } from 'src/app/services/email/email.service';

@Component({
  selector: 'app-deal-details',
  templateUrl: './deal-details.component.html',
  styleUrls: ['./deal-details.component.scss']
})
export class DealDetailsComponent implements OnInit {
  @Input() dealDetailsForm!: FormGroup;
  continents!: any[];
  countries!: any[];
  states!: any[];
  cities!: any[];
  leadSources!: any[];
  maxDate: Date = new Date();
  role: string = '';
  roleMappingId: any;
  emailExistsError: string = '';
  emailExist: boolean = false;
  showNoRecordsFounds: boolean = true

  @Input() isEdit: boolean = false
  @Input() editedCompanyName: string = ''

  @ViewChild('companyNameField') companyNameField!: ElementRef;
    @ViewChild('brandNameField') brandNameField!: ElementRef;
    alreadyExistedCompanyName: string = '';
    alreadyChecked=false;
    isCompanyInputField: boolean = false;
    isSwalConfirmed: boolean = false;
    isHandlingFocus: boolean = false;  
    staffData: any[] = [];

  constructor(
    private masterDataService: MasterDataService, 
    private spinnerService: SpinnerService,
    private localStorageService: LocalStorageService, 
    private emailService: EmailService,
    private endCustomerService: EndCustomerService, private oemManagementService: OemManagementService, private distributorService: DistributorService
  ) {}

  ngOnInit(): void {
    this.loadMasterData()
    this.getAllStaff();
  }  

  getAllStaff(){
    this.role=this.localStorageService.getItem('roleName');
    if(this.role=='OEM'){
      this.roleMappingId=this.localStorageService.getItem('oemId');
    } else if(this.role=='DISTRIBUTOR'){
      this.roleMappingId=this.localStorageService.getItem('distributorId');     
    }
    this.showNoRecordsFounds=false;   
    this.endCustomerService.getAllStaffs(this.role, this.roleMappingId, 100, 0).subscribe((data: any)=>{
      if(data && data?.data && data.data.length>0){
        this.staffData=data.data;  
      }else{
        this.staffData = [];
        this.showNoRecordsFounds=true;
      }
    })
  }  

  onEmailBlur() {
    const emailControl = this.dealDetailsForm?.get('lead')?.get('email');
    const emailValue = emailControl?.value;
    
    if (!emailValue || (emailControl?.errors && !emailControl.errors['emailExists'])) {
      emailControl?.markAsTouched();
      return;
    }
    
    if (emailControl?.errors?.['emailExists']) {
      const { emailExists, ...otherErrors } = emailControl.errors;
      emailControl.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
    }
    
    this.emailService.checkEmailExists(emailValue).subscribe({
      next: () => {
        this.emailExist = false;
        this.emailExistsError = '';
        emailControl?.setErrors(null);
        this.enableFormFields();
      },
      error: (error) => {
        if (error.status === 409) {
          this.emailExist = true;
          this.emailExistsError = 'Email already exists.';
          emailControl?.setErrors({ emailExists: true });
          emailControl?.markAsTouched();
          this.disableFormFields();
        } else {
          console.error("Error checking email:", error);
          this.enableFormFields();
        }
      }
    });
  }
 
  private disableFormFields() {
    const leadGroup = this.dealDetailsForm?.get('lead') as FormGroup;
    if (leadGroup) {
      const emailControl = leadGroup.get('email');
      const hasOnlyEmailExistsError = 
        emailControl?.errors && 
        'emailExists' in emailControl.errors && 
        Object.keys(emailControl.errors).length === 1;
      
      if (hasOnlyEmailExistsError) {
        Object.keys(leadGroup.controls).forEach(controlName => {
          if (controlName !== 'email') {
            leadGroup.get(controlName)?.disable({ emitEvent: false });
          }
        });
      }
    }
  }
  
  private enableFormFields() {
    const leadGroup = this.dealDetailsForm?.get('lead') as FormGroup;
    if (leadGroup) {
      Object.keys(leadGroup.controls).forEach(controlName => {
        if (controlName !== 'email') {
          leadGroup.get(controlName)?.enable({ emitEvent: false });
        }
      });
    }
  }
   
async  loadMasterData() {
    this.spinnerService.showSpinner();
    forkJoin({
      continents: this.masterDataService.getContinents(),
      leadSources: this.masterDataService.getLeadSources()
    }).subscribe({
      next: (results) => {
        this.continents = results.continents.data;
        this.leadSources = results.leadSources.data.map((source: any) => ({ name: source.sourceName }));
        this.setFormValues();
        this.spinnerService.hideSpinner();
      },
      error: (error) => {       
        this.spinnerService.hideSpinner();
      }
    });
  }

  private setFormValues() {
    const lead = this.dealDetailsForm.get('lead')?.value;    
    if (lead) {      
      this.loadCountries([lead.continent]).then(() => {
        this.dealDetailsForm.get('lead')?.get('country')?.setValue(lead.country);

        this.loadStates([lead.country]).then(() => {
          this.dealDetailsForm.get('lead')?.get('state')?.setValue(lead.state);
  
          this.loadCities([lead.state]).then(() => {
            this.dealDetailsForm.get('lead')?.get('city')?.setValue(lead.city);
          });
        });
      });
    }
  }  

  loadCountries(continentNames: string[]): Promise<void> {    
    return new Promise((resolve, reject) => {
      this.masterDataService.getCountries(0, 100, '', continentNames).subscribe({
        next: (data) => {      
          this.countries = data.data;
          resolve();
        },
        error: (error) => {     
          reject(error);
        }
      });
    });
  }

  loadStates(countryNames: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.masterDataService.getStates(0, 500, '', countryNames).subscribe({
        next: (data) => {
          this.states = data.data;
          resolve();
        },
        error: (error) => {        
          reject(error);
        }
      });
    });
  }

  loadCities(stateNames: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.masterDataService.getCities(0, 500, '', stateNames).subscribe({
        next: (data) => {
          this.cities = data.data;
          resolve();
        },
        error: (error) => {        
          reject(error);
        }
      });
    });
  }

  onContinentChange(event: any) {
    const continentName = event?.value;
    this.loadCountries([continentName]);
  }

  onCountryChange(event: any) {
    const countryName = event?.value;
    this.loadStates([countryName]);
  }

  onStateChange(event: any) {
    const stateName = event?.value;
    this.loadCities([stateName]);
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  onAccountTypeChange(selectedValue: string) {
    this.dealDetailsForm.patchValue({ lead: { accountType: selectedValue } });
  }

    isFocusOff(event: any) {
      if(event.target.value === '') {
        this.alreadyChecked = false;
        return;  
      }
      
      if(this.dealDetailsForm?.get('lead')?.get('accountType')?.value === 'OEM'){
        if(!this.alreadyChecked) {
          this.oemManagementService.checkCompanyName(event.target.value).subscribe(res => {
          },
          error => {
            if (error.status === 409) {
              this.swalPopupAppeared(event);
              this.alreadyChecked = false;
              this.alreadyExistedCompanyName = event.target.value;
            }
          }
        )
        }
      } else if (this.dealDetailsForm?.get('lead')?.get('accountType')?.value === 'Distributor') {
        if(!this.alreadyChecked) {
          this.distributorService.checkCompanyName(event.target.value).subscribe(res => {
          },
          error => {
            if (error.status === 409) {
              this.swalPopupAppeared(event);
              this.alreadyChecked = false;
              this.alreadyExistedCompanyName = event.target.value;
            }
          }
        )
        }
      }    
    }
    swalPopupAppeared(event: any){
      Swal.fire({
        position : 'center',
        title: 'Warning',
        text: `${this.dealDetailsForm?.get('lead')?.get('accountType')?.value} company name already exists. Do you still want to continue?`,
        iconHtml: '<img src="./assets/images/group-delete.svg">',
        iconColor : 'white',
        showCancelButton: true,
        showConfirmButton: true,
        confirmButtonText: 'Yes',
        confirmButtonColor : '#0f74a9',
        cancelButtonText: 'No',  
        cancelButtonColor : '#6c757d',
      }).then((result) => {
        if (result.isConfirmed) {
          this.isCompanyInputField = false;
          this.isFocusOn(event);
        } else if(result.isDismissed) {
          this.isCompanyInputField = true;
          this.isFocusOut(event);
        }
      })
    }
  
    isFocusOut(event: any){
      if (this.isHandlingFocus) return;
      if(this.isCompanyInputField) {
        this.isHandlingFocus = true;
        this.companyNameField.nativeElement.focus();
        this.isHandlingFocus = false;
      }
    }
    editEvent(){
      this.isSwalConfirmed = false;
      if(this.alreadyChecked && this.isSwalConfirmed === false) {
        this.companyNameField.nativeElement.focus();
  
      }
    }
    isFocusOn(event: any) {
      if (this.isHandlingFocus) return;
      if(this.isSwalConfirmed && this.alreadyChecked) {
        this.isHandlingFocus = true;
        this.companyNameField.nativeElement.blur();
          this.brandNameField.nativeElement.focus();
        this.isHandlingFocus = false;
      }
    }
    isInputChange(event: any) {
      if(this.dealDetailsForm?.get('lead')?.get('accountType')?.value === null){        
        Swal.fire({
          position : 'center',
          title: 'Warning',
          text: 'Please select anyone of the account type.',
          iconHtml: '<img src="./assets/images/group-delete.svg">',
          iconColor : 'white',
          showConfirmButton: true,
          confirmButtonText: 'Ok',
          confirmButtonColor : '#0f74a9',
        })
        this.dealDetailsForm?.get('lead')?.get('companyName')?.reset();
        return;
      }
      this.alreadyChecked = false;
      this.isSwalConfirmed = false;
      this.isCompanyInputField = false;
  
      if(!this.isEdit && this.dealDetailsForm.get('lead')?.get('companyName')?.value === this.editedCompanyName){
        this.alreadyChecked = true;
      } else {
        if(event.target.value === '') {
      }
       else if(this.dealDetailsForm.get('lead')?.get('companyName')?.value !== this.alreadyExistedCompanyName){
        this.isCompanyInputField = false;
        this.isFocusOut(event);
      } else if(this.dealDetailsForm?.get('lead')?.get('companyName')?.value === this.alreadyExistedCompanyName){
        this.alreadyExistedCompanyName = this.dealDetailsForm.get('lead')?.get('companyName')?.value || '';
        this.alreadyChecked = true;
        this.isSwalConfirmed = true;
        this.swalPopupAppeared(event);
        this.companyNameField.nativeElement.removeEventListener('focus', this.isFocusOn.bind(this));
      }
      }
    }
}